import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SECTIONS, type SectionKey } from '../sections';

export interface StatTileProps {
  readonly section?: SectionKey;
  readonly icon?: ComponentType<{ className?: string; strokeWidth?: number | string }>;
  readonly label: string;
  readonly value: ReactNode;
  readonly subtext?: ReactNode;
  readonly to?: string;
  readonly tone?: 'pos' | 'neg' | 'default';
  readonly numeric?: boolean;
  readonly variant?: 'tint' | 'hero';
}

export function StatTile({
  section,
  icon: Icon,
  label,
  value,
  subtext,
  to,
  tone = 'default',
  numeric = false,
  variant = 'tint',
}: StatTileProps) {
  const sec = section ? SECTIONS[section] : null;
  const hero = variant === 'hero' && !!sec;

  const valueColor = hero
    ? '#ffffff'
    : tone === 'neg'
    ? 'var(--neg)'
    : tone === 'pos'
    ? 'var(--pos)'
    : 'var(--ink)';

  const inner = (
    <div
      className="relative overflow-hidden rounded-[22px] border px-3.5 py-3.5 shadow-sm backdrop-blur-xl"
      style={
        hero
          ? { background: sec!.color, borderColor: 'transparent' }
          : sec
          ? {
              background: `linear-gradient(160deg, rgba(255,255,255,0.22) 0%, ${sec.plate} 100%)`,
              borderColor: sec.edge,
            }
          : { background: 'var(--surface)', borderColor: 'var(--border)' }
      }
    >
      {sec && (
        <span
          className="absolute inset-x-3 top-0 h-1 rounded-b-full"
          style={{ background: hero ? 'rgba(255,255,255,0.28)' : sec.color }}
          aria-hidden
        />
      )}
      <div className="flex items-center gap-1.5">
        {Icon && (
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[11px]"
            style={
              hero
                ? { background: 'rgba(255,255,255,0.16)', color: '#ffffff' }
                : sec
                ? { background: sec.surface, color: sec.color, border: `1px solid ${sec.edge}` }
                : { background: 'var(--surface-raised)', color: 'var(--ink-muted)' }
            }
            aria-hidden
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
        )}
        <span
          className="truncate text-caption font-semibold uppercase tracking-[0.14em]"
          style={{ color: hero ? 'rgba(255,255,255,0.86)' : sec ? sec.color : 'var(--ink-muted)' }}
        >
          {label}
        </span>
      </div>
      <div
        className={['mt-2.5 text-[1.52rem] font-semibold leading-none', numeric ? 'font-mono tabular-nums' : ''].join(' ')}
        style={{ color: valueColor }}
      >
        {value}
      </div>
      {subtext && (
        <div
          className="mt-1.5 text-caption leading-5"
          style={{ color: hero ? 'rgba(255,255,255,0.76)' : 'var(--ink-muted)' }}
        >
          {subtext}
        </div>
      )}
    </div>
  );

  return to ? (
    <Link to={to} className="block transition-transform duration-150 hover:-translate-y-px">
      {inner}
    </Link>
  ) : (
    inner
  );
}
